import { DEFAULT_LOCALE, LOCALES } from './constants';

/** Auth/public path suffixes (without locale prefix). Talents has no MFA / register-2 / register-3. */
export const PUBLIC_AUTH_PATHS = [
  '/login',
  '/register',
  '/recover-password',
  '/set-password',
];

export function isLocale(segment) {
  return LOCALES.includes(segment);
}

export function getLocaleFromPathname(pathname) {
  if (!pathname) {
    return null;
  }
  const segment = pathname.split('/').filter(Boolean)[0];
  return isLocale(segment) ? segment : null;
}

/** Remove leading /:locale from pathname; returns path starting with / */
export function stripLocale(pathname) {
  if (!pathname) {
    return '/';
  }
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && isLocale(parts[0])) {
    const rest = parts.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/**
 * Invitation query params must survive /en /de /fi redirects.
 * Team invite: ?team= (optional ?user-token=)
 * Pool invite: ?pool=
 * Do not require user-token: emails like ?team=<hash>#login are still invitations.
 */
export function hasInvitationSearch(search = '') {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  const hasTeamInvite = Boolean(params.get('team'));
  const hasPoolInvite = Boolean(params.get('pool'));
  const hasUserToken = Boolean(params.get('user-token'));
  return hasTeamInvite || hasPoolInvite || hasUserToken;
}

/** Unauthenticated invitees register; everyone else lands on login. */
export function publicAuthFallbackPath(search = '') {
  return hasInvitationSearch(search) ? '/register' : '/login';
}

/**
 * Build a locale-prefixed path for public routes.
 * @param {string} locale
 * @param {string} path - e.g. '/login' or 'login' or '/recover-password?x=1'
 */
export function withLocale(locale, path = '/') {
  const safeLocale = isLocale(locale) ? locale : DEFAULT_LOCALE;
  let pathname = path || '/';
  let search = '';
  let hash = '';

  const hashIndex = pathname.indexOf('#');
  if (hashIndex !== -1) {
    hash = pathname.slice(hashIndex);
    pathname = pathname.slice(0, hashIndex);
  }
  const searchIndex = pathname.indexOf('?');
  if (searchIndex !== -1) {
    search = pathname.slice(searchIndex);
    pathname = pathname.slice(0, searchIndex);
  }

  pathname = stripLocale(pathname.startsWith('/') ? pathname : `/${pathname}`);
  if (pathname === '/') {
    const fallback = publicAuthFallbackPath(search);
    return `/${safeLocale}${fallback}${search}${hash}`;
  }
  return `/${safeLocale}${pathname}${search}${hash}`;
}

export function isPublicLocaleRoute(pathname) {
  return Boolean(getLocaleFromPathname(pathname));
}

/** Paths authenticated users should not stay on (locale-aware) */
export function isRestrictedAuthPath(pathname) {
  const stripped = stripLocale(pathname);
  return PUBLIC_AUTH_PATHS.includes(stripped);
}
